import { defineStore } from "pinia";
import _ from "lodash";
import { ProcessService } from "@/services/administry.service";
import { ProcesServiceDto } from "@/dto/appointment/appointment.dto";
import { useAuthStore } from "@/store/modules/auth";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

export const useStatisticsStore = defineStore("statistics", {
  state: () => ({
    allBirthList: [] as ProcesServiceDto[],
    allDeathList: [] as ProcesServiceDto[],
    birthList: [] as ProcesServiceDto[],
    deathList: [] as ProcesServiceDto[],
    startDate: null as string | null,
    endDate: null as string | null,
  }),

  getters: {
    totalBirths(state) {
      return state.birthList.length;
    },

    totalDeaths(state) {
      return state.deathList.length;
    },

    statisticsByPeriod(state) {
      const birthCounts = _.groupBy(state.birthList, "created_at");
      const deathCounts = _.groupBy(state.deathList, "created_at");
      const allDates = new Set([...Object.keys(birthCounts), ...Object.keys(deathCounts)]);
      return Array.from(allDates).map((date) => ({
        date: dayjs(date).format("YYYY-MM-DD"),
        births: birthCounts[date]?.length || 0,
        deaths: deathCounts[date]?.length || 0,
      }));
    },

    chartData(state) {
      const monthData = _.groupBy(state.birthList, (item) => dayjs(item.created_at).month());
      const deathMonthData = _.groupBy(state.deathList, (item) => dayjs(item.created_at).month());
      const months = _.range(0, 12);

      return {
        labels: months.map((m) => dayjs().month(m).format("MMM")),
        datasets: [
          {
            label: "Naissances",
            backgroundColor: "#00BCD4",
            data: months.map((m) => monthData[m]?.length || 0),
          },
          {
            label: "Décès",
            backgroundColor: "#F44336",
            data: months.map((m) => deathMonthData[m]?.length || 0),
          },
        ],
      };
    },

    genderBirthChartData(state) {
      const birthBySex = _.countBy(state.birthList, "sex");
      const labels = ["Masculin", "Féminin"];
      const birthData = [birthBySex["male"] || 0, birthBySex["female"] || 0];

      return {
        labels,
        datasets: [
          {
            label: "Naissances",
            backgroundColor: ["#42A5F5", "#FF7043"],
            data: birthData,
          },
        ],
      };
    },

    genderDeathChartData(state) {
      const deathBySex = _.countBy(state.deathList, "sex");
      const labels = ["Masculin", "Féminin"];
      const deathData = [deathBySex["male"] || 0, deathBySex["female"] || 0];

      return {
        labels,
        datasets: [
          {
            label: "Décès",
            backgroundColor: ["#66BB6A", "#EF5350"],
            data: deathData,
          },
        ],
      };
    },
  },

  actions: {
    filterByDateRange(list: ProcesServiceDto[], startDate: string | null, endDate: string | null) {
      if (!startDate || !endDate) return list;
      const start = dayjs(startDate);
      const end = dayjs(endDate);

      if (!start.isValid() || !end.isValid()) {
        console.warn("Invalid date range:", startDate, endDate);
        return list;
      }

      console.log("Filtering with dates:", start.format("YYYY-MM-DD"), "to", end.format("YYYY-MM-DD"));

      return list.filter((item) => {
        const createdAt = dayjs(item.created_at);
        console.log("Checking item created at:", createdAt.format("YYYY-MM-DD"));
        return createdAt.isBetween(start, end, null, "[]");
      });
    },

    async loadBirthList(payload: Record<string, any> = {}) {
      try {
        const service = ProcessService.getInstance(useAuthStore().getAccessToken);
        const response = await service.getBirthList(payload);
        if (response.data && Array.isArray(response.data)) {
          this.allBirthList = response.data;
          this.birthList = _.cloneDeep(response.data);
        }
      } catch (error) {
        console.error("Error loading birth list:", error);
      }
    },

    async loadDeathList(payload: Record<string, any> = {}) {
      try {
        const service = ProcessService.getInstance(useAuthStore().getAccessToken);
        const response = await service.getDeathList(payload);
        if (response.data && Array.isArray(response.data)) {
          this.allDeathList = response.data;
          this.deathList = _.cloneDeep(response.data);
        }
      } catch (error) {
        console.error("Error loading death list:", error);
      }
    },

    async loadAllData(payload: Record<string, any> = {}) {
      try {
        await Promise.all([this.loadBirthList(payload), this.loadDeathList(payload)]);
      } catch (error) {
        console.error("Error loading all data:", error);
      }
    },

    resetData() {
      this.birthList = _.cloneDeep(this.allBirthList);
      this.deathList = _.cloneDeep(this.allDeathList);
    },

    async applyDateFilter(startDate: string | null, endDate: string | null) {
      if (!startDate || !endDate) {
        this.resetData();
        return;
      }
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

      console.log("Applying date filter:", formattedStartDate, "to", formattedEndDate);

      this.startDate = formattedStartDate;
      this.endDate = formattedEndDate;

      this.birthList = this.filterByDateRange(this.allBirthList, formattedStartDate, formattedEndDate);
      this.deathList = this.filterByDateRange(this.allDeathList, formattedStartDate, formattedEndDate);
    },

    async filterLastMonth() {
      const startDate = dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      const endDate = dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      await this.applyDateFilter(startDate, endDate);
    },

    async filterLastQuarter() {
      const startDate = dayjs().subtract(3, "month").startOf("month").format("YYYY-MM-DD");
      const endDate = dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      await this.applyDateFilter(startDate, endDate);
    },

    async filterLastSemester() {
      const startDate = dayjs().subtract(6, "month").startOf("month").format("YYYY-MM-DD");
      const endDate = dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      await this.applyDateFilter(startDate, endDate);
    },

    async filterCurrentYear() {
      const startDate = dayjs().startOf("year").format("YYYY-MM-DD");
      const endDate = dayjs().endOf("year").format("YYYY-MM-DD");
      await this.applyDateFilter(startDate, endDate);
    },

    async filterPreviousYear() {
      const startDate = dayjs().subtract(1, "year").startOf("year").format("YYYY-MM-DD");
      const endDate = dayjs().subtract(1, "year").endOf("year").format("YYYY-MM-DD");
      await this.applyDateFilter(startDate, endDate);
    },
  },
});
